import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Cloud, Sun, Shield, Award, Feather, HelpCircle } from 'lucide-react';
import { LivingAtmosphereHud, TodayHarvestBackdrop, TodayVitals } from '@/components/LivingSky';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTour } from '@/welcome/TourContext';
import { loadWithCache } from '@/lib/offlineCache';
import { OfflineBanner } from '@/components/OfflineBanner';
import {
  Section, WhatNowCard, FocusList, CropsStrip, GardenValueCard,
  SowPlanner, CommunityPeek, DailyTip, WinterModeCard,
} from '@/components/today/TodaySections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FOCUS_KEY = 'hg_today_focus_done';

function isoWeekKey(d = new Date()) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${week}`;
}

// Ported 1:1 from mobile Today (deriveBriefing) — the WHAT/WHY dispatcher brief.
function deriveBriefing(briefing, vitals, planner, name, hour) {
  const tod = (hour < 5 || hour >= 21) ? 'Evening' : hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  const hey = `${tod}${name ? ' ' + name : ''}`;
  const frost = vitals?.frost_risk;
  const nh = vitals?.next_harvest;
  const soil = vitals?.soil_temp;
  const soilStr = soil?.available ? `soil's at ${soil.value}°C` : '';
  const sowNow = planner?.sow_now || [];
  const closing = sowNow.filter((s) => s.closing_soon);
  const monthName = planner?.current_month || new Date().toLocaleString('en', { month: 'long' });
  const prio = (briefing?.priorities || [])[0];
  if (frost && (frost.level === 'high' || frost.level === 'medium')) {
    const lo = frost.tonight_min ?? frost.min_temp;
    return { tag: 'Protect day', what: `${hey}, it's a protect day.`, why: `${frost.label} — lows near ${lo}°C. Cover tender crops before dark.`, cta: { label: 'See what to protect', route: '/weather', icon: Shield } };
  }
  if (nh && (nh.ready || (typeof nh.days_away === 'number' && nh.days_away <= 1))) {
    return { tag: 'Harvest day', what: `${hey}, it's a harvest day.`, why: `${nh.crop_name} is ready to pick${soilStr ? ` and ${soilStr}` : ''}.`, cta: { label: 'Log a harvest', route: '/my-crops', icon: Award } };
  }
  if (sowNow.length) {
    const why = closing.length
      ? `${closing.length} sowing window${closing.length > 1 ? 's' : ''} close this month${soilStr ? ` · ${soilStr}` : ''}.`
      : `${sowNow.length} crop${sowNow.length > 1 ? 's' : ''} suit ${monthName}${soilStr ? ` · ${soilStr}` : ''}.`;
    return { tag: 'Sowing day', what: `${hey}, it's a sowing day.`, why, cta: { label: 'Open Sowing Planner', route: '/season-plan', icon: Sun } };
  }
  if (prio) {
    return { tag: prio.priority === 'critical' ? 'Act today' : 'Worth a look', what: `${hey}.`, why: prio.detail || prio.title, cta: { label: prio.title, route: '/weather', icon: Cloud } };
  }
  return { tag: 'All good', what: `${hey}, your garden's cruising.`, why: briefing?.confidence_message || 'Nothing urgent today — enjoy your garden.', cta: { label: 'Check your crops', route: '/my-crops', icon: Feather } };
}

function Today({ user }) {
  const { openHelp } = useTour();
  const [briefing, setBriefing] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [planner, setPlanner] = useState(null);
  const [valueDash, setValueDash] = useState(null);
  const [nearYou, setNearYou] = useState(null);
  const [plantings, setPlantings] = useState([]);
  const [pool, setPool] = useState([]);
  const [done, setDone] = useState([]);
  const [winter, setWinter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const navigate = useNavigate();
  const weekKey = isoWeekKey();

  const load = useCallback(async () => {
    try {
      const { data, offline: off, savedAt: sa } = await loadWithCache('today', async () => {
        const [b, vi, mp, v, n, pl, tk] = await Promise.all([
          axios.get(`${API}/today/briefing`, { withCredentials: true }),
          axios.get(`${API}/ai/coach-vitals`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/today/monthly-planner`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/value/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/community/people-near-you`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/plantings`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/tasks`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        return {
          briefing: b.data, vitals: vi.data, planner: mp.data, valueDash: v.data, nearYou: n.data,
          plantings: Array.isArray(pl.data) ? pl.data : [], tasks: Array.isArray(tk.data) ? tk.data : [],
        };
      });
      setBriefing(data.briefing);
      setVitals(data.vitals);
      setPlanner(data.planner);
      setValueDash(data.valueDash);
      setNearYou(data.nearYou);
      setPlantings(data.plantings);

      // Only surface tasks that are actually due now (no due date = always, e.g.
      // legacy/manual tasks). Future care tasks stay hidden until their day.
      const endToday = new Date(); endToday.setHours(23, 59, 59, 999);
      const isDue = (t) => !t.due_date || new Date(t.due_date) <= endToday;
      const taskItems = data.tasks.filter((t) => !t.completed && !t.skipped && isDue(t)).map((t) => ({ key: `task:${t.task_id}`, title: t.title, detail: t.description, isTask: true, taskId: t.task_id, taskType: t.task_type }));
      const prioItems = (data.briefing?.priorities || []).map((pr) => ({ key: `prio:${pr.type}:${pr.title}`, title: pr.title, detail: pr.detail, isTask: false }));
      setPool([...taskItems, ...prioItems]);

      let store = {};
      try { store = JSON.parse(localStorage.getItem(FOCUS_KEY) || '{}'); } catch { store = {}; }
      setDone(store[weekKey] || []);
      setOffline(off);
      setSavedAt(sa);

      if (!off) {
        axios.post(`${API}/notifications/generate`, {}, { withCredentials: true }).catch(() => {});
        // Winter / off-season mode (shown only when active for the grower's zone).
        // `?winter_month=N` (captured to sessionStorage at app entry) is a harmless preview override.
        let wm = null;
        try { wm = sessionStorage.getItem('hg_winter_preview') || new URLSearchParams(window.location.search).get('winter_month'); } catch { wm = null; }
        axios.get(`${API}/winter-mode${wm ? `?month=${wm}` : ''}`, { withCredentials: true })
          .then((r) => setWinter(r.data)).catch(() => {});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [weekKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [load]);

  const tickFocus = (f) => {
    const next = [...done, f.key];
    setDone(next);
    try { localStorage.setItem(FOCUS_KEY, JSON.stringify({ [weekKey]: next })); } catch { /* ignore */ }
    if (f.isTask && f.taskId) axios.patch(`${API}/tasks/${f.taskId}`, { completed: true }, { withCredentials: true }).catch(() => {});
  };

  const skipFocus = (f) => {
    const next = [...done, f.key];
    setDone(next);
    try { localStorage.setItem(FOCUS_KEY, JSON.stringify({ [weekKey]: next })); } catch { /* ignore */ }
    if (f.isTask && f.taskId) axios.patch(`${API}/tasks/${f.taskId}`, { skipped: true }, { withCredentials: true }).catch(() => {});
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const name = user?.name ? user.name.split(' ')[0] : '';
  const g = (briefing?.greeting || '').toLowerCase();
  const hour = typeof briefing?.local_hour === 'number'
    ? briefing.local_hour
    : (g.includes('evening') ? 20 : g.includes('afternoon') ? 14 : 9);
  const brief = deriveBriefing(briefing, vitals, planner, name, hour);
  const activePlantings = (plantings || []).filter((p) => !['harvest', 'failed', 'finished'].includes(p.stage));
  const visibleFocus = pool.filter((f) => !done.includes(f.key)).slice(0, 3);

  const communityOn = !(user?.zen_mode) && (user?.community_enabled !== false);
  const hasSow = !!(planner?.sow_now?.length || planner?.coming_up?.length);
  const hasCommunity = !!nearYou?.trending_crops?.length;
  const hasTip = !!briefing?.daily_tip;
  const weekValue = valueDash?.totals?.week;
  const seasonValue = valueDash?.totals?.season;
  const cur = valueDash?.currency_symbol || '$';

  return (
    <Layout user={user}>
      {/* Help Centre launcher */}
      <button onClick={openHelp} data-testid="help-fab" title="Help Centre"
        className="hg-glass hg-press hg-glow fixed bottom-6 right-6 z-30 flex items-center justify-center rounded-full shadow-lg"
        style={{ width: 52, height: 52 }}>
        <HelpCircle className="w-6 h-6" style={{ color: '#1A4331' }} />
      </button>
      {/* Harvest photo canvas + Living Weather (mirrors mobile today-harvest-bg) */}
      <TodayHarvestBackdrop weatherRaw={briefing?.weather_raw} localHour={briefing?.local_hour} />

      <div className="space-y-6">
        {offline && <OfflineBanner savedAt={savedAt} onRetry={load} />}
        {/* Greeting (mobile HUD greeting: caption date + big greeting) */}
        <div>
          <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#5C665C' }}>{briefing?.date}</p>
          <h1 data-testid="today-greeting" className="text-4xl sm:text-5xl font-bold tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A4331' }}>
            {briefing?.greeting || 'Hello'}
          </h1>
        </div>

        {/* Weather HUD — Living Sky atmosphere card + frosted vitals chips (mirrors mobile TodayHud) */}
        <div data-tour="today-weather">
          <LivingAtmosphereHud weatherRaw={briefing?.weather_raw} localHour={briefing?.local_hour} />
          <TodayVitals vitals={vitals} />
        </div>

        {/* 1 · Dispatcher briefing */}
        <div data-tour="today-advice"><Section><WhatNowCard brief={brief} onGo={() => navigate(brief.cta.route)} /></Section></div>

        {/* 1.5 · Winter / off-season mode (only when active for the grower's zone) */}
        {winter?.active && (
          <Section><WinterModeCard data={winter} onCrop={(id) => navigate(`/crops/${id}`)} onGo={(url) => navigate(url)} /></Section>
        )}

        {/* 2 · This week's focus */}
        <Section><FocusList items={visibleFocus} onTick={tickFocus} onSkip={skipFocus} /></Section>

        {/* 3 · Your growing crops */}
        <Section>
          <CropsStrip
            plantings={activePlantings}
            onOpen={() => navigate('/my-crops')}
            onSeeAll={() => navigate('/my-crops')}
            onEmpty={() => navigate('/crops')}
          />
        </Section>

        {/* 4 · This week's value */}
        <Section><GardenValueCard weekValue={weekValue} seasonValue={seasonValue} cur={cur} onOpen={() => navigate('/value')} /></Section>

        {/* 5 · What to sow this month */}
        {hasSow && <Section><SowPlanner planner={planner} /></Section>}

        {/* 6 · Growing near you */}
        {hasCommunity && communityOn && <Section><CommunityPeek nearYou={nearYou} onOpen={() => navigate('/community')} /></Section>}

        {/* 7 · Daily tip */}
        {hasTip && <Section><DailyTip tip={briefing.daily_tip} /></Section>}
      </div>
    </Layout>
  );
}

export default Today;
